import { ID } from '../types/UtilityTypes'
import { RootState } from '../redux/root'
import { Task } from '../responses/tasks'
import { ProjectWorkObject } from '../responses/objects'
import { Project } from '../responses/projects'
import { isDefined, isDefinedArray } from './utils'
import { Tag } from '../responses/tags'
import { SortDirection } from '../constants/direction'
import { Comment } from '../responses/comments'
import { NonEmptyArray } from 'fp-ts/es6/NonEmptyArray'
import { pipe } from 'fp-ts/es6/function'
import * as A from 'fp-ts/es6/Array'
import createCachedSelector from 're-reselect'

export type EmptySelector<Output> = (state: RootState) => Output
export type Selector<Input, Output> = (input: Input) => EmptySelector<Output>

export function pipeSelectors<T1, T2>(data: T1, s1: Selector<T1, T2>): EmptySelector<T2>
export function pipeSelectors<T1, T2, T3>(data: T1, s1: Selector<T1, T2>, s2: Selector<T2, T3>): EmptySelector<T3>
export function pipeSelectors<T1, T2, T3, T4>(data: T1, s1: Selector<T1, T2>, s2: Selector<T2, T3>, s3: Selector<T3, T4>): EmptySelector<T4>
export function pipeSelectors(data: unknown, ...selectors: Selector<unknown, unknown>[]): EmptySelector<unknown> {
    return state => {
        let result = data
        selectors.forEach(selector => result = selector(result)(state))
        return result
    }
}

export function flowSelectors<T1, T2>(s1: Selector<T1, T2>): Selector<T1, T2>
export function flowSelectors<T1, T2, T3>(s1: Selector<T1, T2>, s2: Selector<T2, T3>): Selector<T1, T3>
export function flowSelectors<T1, T2, T3, T4>(s1: Selector<T1, T2>, s2: Selector<T2, T3>, s3: Selector<T3, T4>): Selector<T1, T4>
export function flowSelectors(...selectors: Selector<unknown, unknown>[]): Selector<unknown, unknown> {
    return (data: unknown) => state => {
        let result = data
        selectors.forEach(selector => result = selector(result)(state))
        return result
    }
}

export const mapSelector = <T1, T2>(selector: Selector<T1, T2 | undefined>): Selector<T1[], T2[]> => (data: T1[]) => (state: RootState): T2[] => {
    const result = data.map(item => selector(item)(state)).filter(isDefined)
    return isDefinedArray(result) ? result : []
}

export const flatMapSelector = <T1, T2>(selector: Selector<T1, (T2 | undefined)[]>): Selector<T1[], T2[]> => (data: T1[]) => (state: RootState): T2[] => {
    const result = data.flatMap(item => selector(item)(state)).filter(isDefined)
    return isDefinedArray(result) ? result : []
}

// tasks

export const taskSelector = (taskId?: ID) => (state: RootState): Task | undefined => taskId && state.data.task.byId[taskId] || undefined
export const memoizedTaskSelector = createCachedSelector(
    (state: RootState, taskId?: ID) => taskId && state.data.task.byId[taskId] || undefined,
        task => task
)((state, id) => id)

export const lineSelector: Selector<ID | undefined, ID[]> = parentId => state => parentId && [parentId, ...childrenSelector(parentId)(state)] || []
export const lineTasksSelector: Selector<ID | undefined, Task[]> = parentId => state => {
    return pipeSelectors(parentId, lineSelector, mapSelector(taskSelector))(state).filter(isDefined)
}
export const taskLinesSelector: Selector<ID | undefined, NonEmptyArray<Task>[]> = objectId => state => pipe(
    state,
    parentsSelector(objectId),
    A.map(parentId => lineTasksSelector(parentId)(state)),
    A.filter(A.isNonEmpty)
)

export const objectTasksSelector = (objectId?: ID) => (state: RootState): ID[] => {
    if (objectId) {
        return parentsSelector(objectId)(state).flatMap(parentId => [parentId, ...childrenSelector(parentId)(state)])
    }
    return []
}
export const userTasksSelector = (userId?: ID) => (state: RootState): ID[] => userId && state.data.task.byUser[userId] || []
export const childrenSelector = (parentId?: ID) => (state: RootState): ID[] => parentId && state.data.task.byParent[parentId] || []
export const parentsSelector = (objectId?: ID) => (state: RootState): ID[] => state.data.task.byObject[objectId ?? 0] || []

export const tasksSelector = mapSelector(taskSelector)
export const taskResultsSelector: Selector<ID, ID[]> = taskId => state => state.data.task.results[taskId] ?? []
export const commentSelector: Selector<ID, Comment | undefined> = commentId => state => state.global.history.resources.comment[commentId]
export const taskCommentsSelector = flowSelectors(taskResultsSelector, mapSelector(commentSelector))
export const lineCommentsSelector = flowSelectors(lineSelector, flatMapSelector(taskResultsSelector), mapSelector(commentSelector))
export const tasksCommentsSelector = flowSelectors(flatMapSelector(taskResultsSelector), mapSelector(commentSelector))

export const objectSelector = (objectId?: ID) => (state: RootState): ProjectWorkObject | undefined => {
    if (objectId) {
        return state.data.object.byId[objectId]
    }
}

export const objectsSelector = mapSelector(objectSelector)

export const projectObjectsSelector = (projectId?: ID) => (state: RootState): ID[] => projectId && state.data.object.byProject[projectId] || []


export const projectSelector = (projectId?: ID) => (state: RootState): Project | undefined => {
    if (projectId !== undefined) {
        return state.data.project.byId[projectId]
    }
}

export const projectsSelector = mapSelector(projectSelector)

export const projectTasksSelector = flowSelectors(projectObjectsSelector, flatMapSelector(objectTasksSelector), mapSelector(taskSelector))

export const tagsSelector = (objectId: ID) => (state: RootState): Tag[] => state.data.tag[objectId] || []
export const directionSelector: EmptySelector<SortDirection> = state => state.global.user.data?.settings.history_direction ?? SortDirection.DESC

export const onlineSelector: Selector<ID | undefined, boolean> = id => state => id ? state.data.users.online[id]: false